import PRODUCT from '@/web/store/types/product'
import getPath from '@/tools/get-path'

const priceObject = ({ isFlashSale, regularPrice, timerPrice }) => {
  return isFlashSale && timerPrice || regularPrice
}

const getPriceValues = state => {
  return priceObject(state).values
}

const getPropertyOfPriceValues = property => state => {
  return getPriceValues(state)[property]
}

const getPriceColors = state => {
  return priceObject(state).colors
}

export default {
  [PRODUCT.GETTERS.GET_SALE_PRICE]: state => {
    const { sale, listed } = getPriceValues(state)['price']

    return typeof sale === 'number' ? sale : listed
  },
  [PRODUCT.GETTERS.GET_LISTED_PRICE]: state => {
    const { listed } = getPriceValues(state)['price']

    return listed
  },
  [PRODUCT.GETTERS.GET_TAG_TEXT]: getPropertyOfPriceValues('tag_text'),
  [PRODUCT.GETTERS.GET_PRICE_SUBTITLE]: getPropertyOfPriceValues('price_subtitle'),
  [PRODUCT.GETTERS.GET_TIMER_TEXT]: getPropertyOfPriceValues('timer_text'),
  [PRODUCT.GETTERS.GET_TIMER_TYPE]: getPropertyOfPriceValues('timer_type'),
  [PRODUCT.GETTERS.GET_EXPIRES_AT]: getPropertyOfPriceValues('expires_at'),
  [PRODUCT.GETTERS.GET_PRODUCT_ID]: state => {
    return state.id
  },
  [PRODUCT.GETTERS.GET_PRODUCT]: state => {
    return state
  },
  [PRODUCT.GETTERS.GET_PRODUCT_BRAND]: state => {
    return state.brand
  },
  [PRODUCT.GETTERS.GET_BRAND_HREF]: state => {
    const { name, slug, verified } = state.brand

    if (!slug) {
      return ''
    }

    return verified === false ?
      getPath('search', { slug, q: name }) :
      getPath('search', { slug, b: slug })
  },
  [PRODUCT.GETTERS.GET_IS_FLASH_SALE]: ({ isFlashSale }) => {
    return isFlashSale
  },
  [PRODUCT.GETTERS.GET_COLOR]: state => colorKey => {
    return getPriceColors(state)[colorKey]
  },
  [PRODUCT.GETTERS.GET_HAS_PENDING_CHECKOUT]: ({ purchase }) => {
    return !!purchase.release_to_buy_at
  },
  [PRODUCT.GETTERS.GET_OFFER]: ({offer}) => {
    return offer
  },
  [PRODUCT.GETTERS.GET_IS_PURCHASE_AVAILABLE]: state => {
    return state.ownerId !== state.currentUserId && state.purchase.state === 'published'
  },
  [PRODUCT.GETTERS.GET_IS_OFFER_AVAILABLE]: ({ offer }) => {
    return offer && offer.available
  },
  [PRODUCT.GETTERS.GET_OFFER_STATUS]: ({ offer }) => {
    return offer && !offer.available && offer.state !== 'accepted' && offer.unavailable_reason
  },
  [PRODUCT.GETTERS.GET_CAN_SELL_WITH_BUNDLE]: ({ purchase }) => {
    return purchase.can_sell_with_bundle
  },
  [PRODUCT.GETTERS.GET_UNAVAILABLE_REASON]: ({ purchase }) => {
    return !purchase.release_to_buy_at && purchase.unavailable_reason
  },
  [PRODUCT.GETTERS.GET_IS_PRODUCT_IN_BUNDLE]: ({ purchase }) => {
    return purchase.product_in_bundle
  },
  [PRODUCT.GETTERS.GET_RELEASE_TO_BUY_AT]: ({ purchase }) => {
    return purchase.release_to_buy_at
  },
  [PRODUCT.GETTERS.GET_MORE_FROM_STORE]: state => {
    return state.moreFromStore
  },
  [PRODUCT.GETTERS.GET_RELATED_PRODUCTS]: state => {
    return state.relatedProducts
  },
  [PRODUCT.GETTERS.GET_CURRENT_USER_ID]: state => {
    return state.currentUserId
  },
  [PRODUCT.GETTERS.GET_NAVIGATION_HISTORY]: state => {
    return state.navigationHistory
  },
  [PRODUCT.GETTERS.GET_IS_FETCHING_PRICE]: state => {
    return state.isFetchingPrice
  },
  [PRODUCT.GETTERS.GET_PURCHASE_STATE]: state => {
    return state.purchase.state
  },
  [PRODUCT.GETTERS.GET_IS_BANKSLIP_AVAILABLE]: state => {
    return state.purchase.bankslip_available
  },
  [PRODUCT.GETTERS.GET_IS_PIX_AVAILABLE]: state => {
    return state.purchase.pix_available
  },
  [PRODUCT.GETTERS.GET_PAYMENT_OPTIONS_DESCRIPTION]: state => {
    return state.purchase.payment_options_description
  },
  [PRODUCT.GETTERS.GET_IS_READY]: state => {
    return state.isReady
  },
  [PRODUCT.GETTERS.GET_IS_PRODUCT_OWNER]: state => {
    return state.ownerId === state.currentUserId
  },
  [PRODUCT.GETTERS.GET_HAS_FILLED_META_TAGS]: state => {
    return state.hasFilledMetaTags
  },
  [PRODUCT.GETTERS.GET_SHIPPING]: state => {
    return state.shipping
  },
  [PRODUCT.GETTERS.GET_IS_SHIPPING_PRICE_ENABLE]: state => {
    return state.isShippingPriceEnable
  },
  [PRODUCT.GETTERS.GET_SERVICE_TAX]: state => {
    return state.serviceTax
  },
  [PRODUCT.GETTERS.GET_COUPON]: state => {
    return state.coupon
  },
  [PRODUCT.GETTERS.GET_HAS_COPIED_COUPON_CODE]: state => {
    return state.hasCopiedCouponCode
  },
  [PRODUCT.GETTERS.GET_SIMILAR_PRODUCT_INFO]: state => {
    return state.similarProductInfo
  },
  [PRODUCT.GETTERS.GET_PRODUCT_ACTIONS]: state => {
    const hasAddToBagButtonData = !!state.actions.add_to_bag
    const hasPurchaseButtonData = !!state.actions.purchase
    const hasOfferData = !!state.actions.offer
    const crossSellAction = 'bottom_sheet'

    return {
      addToBag: {
        visible: hasAddToBagButtonData ? state.actions.add_product?.visible : true,
        isInCrossSellExperiment: state.actions.add_product?.action === crossSellAction
      },
      purchase: {
        visible: hasPurchaseButtonData ? state.actions.purchase?.visible : true,
        isInCrossSellExperiment: state.actions.purchase?.action === crossSellAction
      },
      offer: {
        visible: hasOfferData ? state.actions.offer?.visible : true,
        isInCrossSellExperiment: state.actions.offer?.action === crossSellAction
      }
    }
  },
  [PRODUCT.GETTERS.GET_CROSS_SELL_METRIC_DATA]: state => {
    return {
      purchase_action: state?.actions?.purchase.action,
      add_product_action: state?.actions?.add_product?.action,
      is_bottom_sheet_eligible: state?.actions?.is_bottom_sheet_eligible
    }
  },
  [PRODUCT.GETTERS.GET_PRODUCT_BANNER]: state => {
    return state.banner
  },
  [PRODUCT.GETTERS.GET_IS_FETCHING_ACTIONS]: state => {
    return state.isFetchingActions
  },
  [PRODUCT.GETTERS.GET_SHOW_BUNDLE_SUGGESTION_MODAL]: state => {
    return state.isProductSuggestionModalOpen
  },
  [PRODUCT.GETTERS.GET_IS_HAND_DELIVER]: state => {
    return state.shipping?.type === 'hand_deliver'
  },
  [PRODUCT.GETTERS.GET_RELATED_PRODUCT_SEARCH_ID]: state => {
    return state.relatedProductSearchId
  },
  [PRODUCT.GETTERS.GET_MORE_FROM_STORE_SEARCH_ID]: state => {
    return state.setMoreFromStoreSearchId
  }
}
